
import { defineComponent, h, ref } from 'vue';
import { FormInst, FormItemRule } from 'naive-ui';
import { toast, notification } from '../utils/notification.util';
import { SelectOption } from 'naive-ui/lib/select/src/interface';
import HoundSelectOption from './shared/HoundSelectOption.vue';
import { http } from '../services';
import { HoundModel, HoundQueryDto, FindAllResponse, emptyAddress } from '../../../common';
import { MicroPayment } from '../../../common/ethers-v5/Queues';
import { breedingCurrency, breedingPlatformFee, breedingTransactionCost, breedingTransactionCostCurrency } from '../data/global';
import BaseNotificationContent from './shared/BaseNotificationContent.vue';
import { EthersService as eth } from '../services';
import { getCurrencyLabel } from '../../../common/inventories/index';
import { BigNumber } from 'ethers';

export default defineComponent({
  emits: ['cancel', 'bredHounds'],
  setup(_, { emit }) {
    const formRef = ref<FormInst | null>(null);
    const cancel = () => {
      emit('cancel');
    };

    const formValue = ref({
      maleHoundId: null,
      femaleHoundId: null,
    });

    const maleHoundOptions = ref<SelectOption[]>();
    const femaleHoundOptions = ref<SelectOption[]>();
    const maleHounds = ref<HoundModel[]>([]);
    const femaleHounds = ref<HoundModel[]>([]);
    const filters = new HoundQueryDto({
      limit: null,
      offset: null,
      orderBy: 'lastBreed-DESC'
    });

    const renderLabel = (option: SelectOption) => {
      const hound = [...maleHounds.value, ...femaleHounds.value].find(h => h.token_id === option.value);
      return h(
        HoundSelectOption,
        {
          hound: hound,
          style: 'height: 5rem'
        }
      )
    };

    const renderTag = ({ option }) => {
      const hound = [...maleHounds.value, ...femaleHounds.value].find(h => h.token_id === option.value);
      return h(
        HoundSelectOption,
        {
          hound: hound,
          style: 'height: 5rem',
          key: hound.id
        }
      );
    };
    
    const loading = ref<boolean>(false);
    
    http.getOwnHounds({...filters, sex: 1, running: false, hasLibido: true}).then((res: FindAllResponse<HoundModel>) => {
      res.data.forEach(el => {
        maleHounds.value.push(new HoundModel(el));
      })
      maleHoundOptions.value = res.data.map((h: HoundModel) => ({
        value: h.token_id,
        label: h.name
      }));
    });

    http.getOwnHounds({...filters, sex: 2, running: false, hasLibido: true}).then((res: FindAllResponse<HoundModel>) => {
      res.data.forEach(el => {
        femaleHounds.value.push(new HoundModel(el));
      })
      femaleHoundOptions.value = res.data.map((h: HoundModel) => ({
        value: h.token_id,
        label: h.name
      }));
    });

    const currencyName = eth.currenciesInfo.find(c => c.value === breedingCurrency.value)?.label;
    const transactionCurrencyName = eth.currenciesInfo.find(c => c.value === breedingTransactionCostCurrency.value)?.label;

    const breed = () => {
      formRef.value?.validate(async (errors) => {
        if (errors) {
          toast.error('Invalid values');
          return;
        }
        try {
          loading.value = true;
          const breedCost: [MicroPayment.StructStructOutput, MicroPayment.StructStructOutput, MicroPayment.StructStructOutput] = await eth.hounds.getBreedCosts(String(formValue.value.femaleHoundId));

          let totalDefaultCurrencyToPay: BigNumber = BigNumber.from(0);
          let customApprovalsToMake: Array<{ currency: string, amount: string | BigNumber }> = [];

          for ( let i = 0 , l = breedCost.length - 1; i < l ; ++i ) {
            if ( breedCost[i].currency === emptyAddress ) {
              totalDefaultCurrencyToPay = totalDefaultCurrencyToPay.add(breedCost[i].amount);
            } else {
              let exists = -1;
              for ( let j = 0 , k = customApprovalsToMake.length ; j < k ; ++j ) {
                if ( customApprovalsToMake[j].currency === breedCost[i].currency ) {
                  exists = j;
                  break;
                }
              }
              if ( exists === -1 ) {
                customApprovalsToMake.push({
                  currency: breedCost[i].currency,
                  amount: breedCost[i].amount
                });
              } else {
                customApprovalsToMake[exists].amount = BigNumber.from(customApprovalsToMake[exists].amount).add(breedCost[i].amount);
              }
            }
          }

          for ( let i = 0 , l = customApprovalsToMake.length ; i < l ; ++i ) {
            const customTokenInterface = eth.getCustomTokenInterface(customApprovalsToMake[i].currency);
            const alreadyApproved = await customTokenInterface.allowance(eth.userAddress, eth.contractAddresses.PAYMENTS);
              if (alreadyApproved.lt(customApprovalsToMake[i].amount)) {
                const approveTx = await eth.getCustomTokenInterface(
                  customApprovalsToMake[i].currency
                ).approve(
                  eth.contractAddresses.PAYMENTS, 
                  BigNumber.from("1000000000000000000").gt(customApprovalsToMake[i].amount) ? "1000000000000000000" : customApprovalsToMake[i].amount
                );
                await approveTx.wait();
              }
          }

          const tx = await eth.hounds.breedHounds(
            String(formValue.value.maleHoundId),
            String(formValue.value.femaleHoundId), {
              value: totalDefaultCurrencyToPay,
              gasLimit: 3000000
            }
          );

          await tx.wait();
          const maleHound = maleHounds.value.find(h => h.token_id === formValue.value.maleHoundId);
          const femaleHound = femaleHounds.value.find(h => h.token_id === formValue.value.femaleHoundId);

          const distinctCosts: Array<{ amount: BigNumber, currency: string }> = [];
          breedCost.forEach(bc => {
            const bcf = { amount: bc.amount, currency: bc.currency };
            const existingCost = distinctCosts.find(dc => dc.currency === bc.currency);
            if (existingCost) existingCost.amount = BigNumber.from(existingCost.amount).add(bc.amount);
            else distinctCosts.push(bcf);
          })

          notification.create({
            title: 'Hounds Bred',
            description: `You've successfully bred ${maleHound.name} and ${femaleHound.name}.`,
            content: () => h(
              BaseNotificationContent,
              {
                infoElements: [
                  { label: 'Male Hound', content: maleHound.name },
                  { label: 'Female Hound', content: femaleHound.name },
                  {
                    label: 'Breeding Fee',
                    content: distinctCosts
                      .map(cost => {
                        if (cost.amount.gt(0)) {
                          return `${eth.utils.formatEther(cost.amount)} ${getCurrencyLabel(eth.chainId, cost.currency)}`
                        }
                      })
                      .join(' | ')
                  }
                ]
              }
            ),
            meta: new Date().toUTCString()
          });
          emit('bredHounds');
          toast.success('Successful breed');
        } catch(err) {
          toast.error('Transaction Failed');
          console.log(err);
        } finally {
          loading.value = false;
          cancel();
        }
      })
    };

    const rules = {
      maleHoundId: {
        trigger: ['blur', 'input'],
        validator: (rule: FormItemRule, value: any) => {
          return new Promise<void>((resolve, reject) => {
            if (value === null) reject(Error('Male Hound is required'));
            resolve();
          });
        }
      },
      femaleHoundId: {
        trigger: ['blur', 'input'],
        validator: (rule: FormItemRule, value: any) => {
          return new Promise<void>((resolve, reject) => {
            if (value === null) reject(Error('Female Hound is required'));
            resolve();
          });
        }
      },
    }

    return {
      formRef,
      formValue,
      cancel,
      breed,
      loading,
      rules,
      maleHoundOptions,
      femaleHoundOptions,
      renderLabel,
      renderTag,
      platformFee: breedingPlatformFee,
      transactionCost: breedingTransactionCost,
      currencyName,
      transactionCurrencyName
    };
  },
})
